import React from 'react';
import { FiX, FiDownload, FiFileText, FiAlertCircle } from 'react-icons/fi';

const InstructionsModal = ({ show, onClose, type, onDownloadTemplate }) => {
  if (!show) return null;

  const getInstructions = () => {
    switch (type) {
      case 'accounts':
        return {
          title: 'Account Import Instructions',
          description: 'Upload your account data using CSV, Excel, or JSON format',
          requiredFields: [
            { field: 'name', description: 'Account name (e.g., "Main Checking")', required: true },
            { field: 'type', description: 'Account type: Current, Saving, Checking, or Investment', required: true },
            { field: 'currency', description: 'Currency code (e.g., USD, EUR, GBP)', required: true },
            { field: 'balance', description: 'Initial balance (numeric value)', required: true }
          ],
          sampleData: [
            { name: 'Main Checking', type: 'Current', currency: 'USD', balance: '5000.00' },
            { name: 'Savings Account', type: 'Saving', currency: 'USD', balance: '15000.50' },
            { name: 'Investment Portfolio', type: 'Investment', currency: 'EUR', balance: '8500.75' }
          ],
          notes: [
            'Balance should be a positive number',
            'Currency must be a valid 3-letter code (USD, EUR, etc.)',
            'Account names should be unique',
            'Type must be one of: Current, Saving, Checking, Investment'
          ]
        };
      case 'income':
        return {
          title: 'Income Import Instructions',
          description: 'Upload your income data using CSV, Excel, or JSON format',
          requiredFields: [
            { field: 'name', description: 'Income source name (e.g., "Salary")', required: true },
            { field: 'source', description: 'Income source (e.g., "Business, Salary etc")', required: true },
            { field: 'frequency', description: 'Frequency: Monthly, Weekly, Yearly, Daily, OneTime', required: true },
            { field: 'amount', description: 'Income amount (numeric value)', required: true },
            { field: 'currency', description: 'Currency code (e.g., USD, EUR)', required: true },
            { field: 'bonus', description: 'Bonus amount (optional, numeric)', required: false },
            { field: 'account', description: 'Account Name where income is received', required: true },
            { field: 'lastDepositDate', description: 'Last deposit date in YYYY-MM-DD format', required: true }
          ],
          sampleData: [
            { 
              name: 'Monthly Salary', 
              source: 'Salary', 
              frequency: 'Monthly', 
              amount: '8000', 
              currency: 'USD', 
              bonus: '1000', 
              account: 'HBL',
              lastDepositDate: '2025-01-15'
            },
            { 
              name: 'Daily Income', 
              source: 'Business', 
              frequency: 'Daily', 
              amount: '100', 
              currency: 'USD', 
              bonus: '0', 
              account: 'UBL',
              lastDepositDate: '2025-01-10'
            },
            { 
              name: 'Weekly Allowance', 
              source: 'Other', 
              frequency: 'Weekly', 
              amount: '500', 
              currency: 'USD', 
              bonus: '0', 
              account: 'HBL',
              lastDepositDate: '2025-01-08'
            },
            { 
              name: 'OneTime Bonus', 
              source: 'Other', 
              frequency: 'OneTime', 
              amount: '5000', 
              currency: 'USD', 
              bonus: '0', 
              account: 'UBL',
              lastDepositDate: '2025-12-25'
            }
          ],
          notes: [
            'Amount and bonus should be positive numbers',
            'Source Must be either of these enum (Salary, Investment, Business, Other)',
            'Frequency must be: Monthly, Weekly, Yearly, Daily, or OneTime',
            'Account must be a valid account name from your accounts',
            'Currency must match your account currency or base currency',
            'lastDepositDate is required and must be in YYYY-MM-DD format',
            'Manual entry through UI allows detailed scheduling with depositType, depositDay, and depositMonth selection'
          ]
        };
      case 'budget':
        return {
          title: 'Budget Import Instructions',
          description: 'Upload your budget data using CSV, Excel, or JSON format',
          requiredFields: [
            { field: 'itemName', description: 'Budget item name (e.g., "Rent", "Groceries")', required: true },
            { field: 'category', description: 'Budget category (e.g., "Housing", "Food")', required: true },
            { field: 'frequency', description: 'Frequency: Monthly, Weekly, Yearly, OneTime', required: true },
            { field: 'plannedCost', description: 'Planned cost amount (numeric value)', required: true },
            { field: 'actualCost', description: 'Actual cost amount (optional, numeric)', required: false },
            { field: 'paymentAccountId', description: 'Account name for payments', required: true },
            { field: 'tags', description: 'Tags (optional, e.g., "Subscription")', required: false }
          ],
          sampleData: [
            { 
              itemName: 'Rent', 
              category: 'Housing', 
              frequency: 'Monthly', 
              allocationPercent: '30', 
              plannedCost: '1500', 
              actualCost: '1500', 
              paymentAccountId: 'Main Checking',
              tags: ''
            },
            { 
              itemName: 'Netflix', 
              category: 'Entertainment', 
              frequency: 'Monthly', 
              allocationPercent: '2', 
              plannedCost: '15', 
              actualCost: '15', 
              paymentAccountId: 'Main Checking',
              tags: 'Subscription'
            },
            { 
              itemName: 'Groceries', 
              category: 'Food', 
              frequency: 'Weekly', 
              allocationPercent: '15', 
              plannedCost: '100', 
              actualCost: '', 
              paymentAccountId: 'Main Checking',
              tags: ''
            },
            { 
              itemName: 'Car Insurance', 
              category: 'Insurance', 
              frequency: 'Yearly', 
              allocationPercent: '8', 
              plannedCost: '1200', 
              actualCost: '', 
              paymentAccountId: 'Savings Account',
              tags: ''
            }
          ],
          notes: [
            'AllocationPercent should be between 0-100',
            'PlannedCost and actualCost should be positive numbers',
            'Frequency must be: Monthly, Weekly, Yearly, or OneTime',
            'PaymentAccountId must be a valid account name from your accounts',
            'Tags can include "Subscription" for subscription-based items',
            'ActualCost is optional - leave empty if not yet spent',
            'You can exceed available budget - system allows overallocation',
            'Categories help organize your budget items for better tracking'
          ]
        };
      case 'savings':
        return {
          title: 'Savings Import Instructions',
          description: 'Upload your savings data using CSV, Excel, or JSON format',
          requiredFields: [
            { field: 'title', description: 'Savings goal title (e.g., "Car Savings", "Emergency Fund")', required: true },
            { field: 'targetAmount', description: 'Target amount to save (numeric value)', required: true },
            { field: 'currentAmount', description: 'Current amount saved (numeric, can be 0)', required: true },
            { field: 'targetDate', description: 'Target date in YYYY-MM-DD format', required: true },
            { field: 'paymentAccountId', description: 'Account name (source of funds)', required: true },
            { field: 'destinationAccountId', description: 'Destination account name (where savings are deposited)', required: true },
            { field: 'notes', description: 'Notes about the saving goal (optional)', required: false }
          ],
          sampleData: [
            { 
              title: 'Car Savings', 
              targetAmount: '25000', 
              currentAmount: '5000', 
              targetDate: '2025-12-31', 
              paymentAccountId: 'Main Savings',
              destinationAccountId: 'Main Savings',
              notes: 'Saving for a new car'
            },
            { 
              title: 'Emergency Fund', 
              targetAmount: '10000', 
              currentAmount: '2500', 
              targetDate: '2025-06-30', 
              paymentAccountId: 'Emergency Account',
              destinationAccountId: 'Emergency Account',
              notes: '6 months of expenses'
            },
            { 
              title: 'Vacation Fund', 
              targetAmount: '5000', 
              currentAmount: '1200', 
              targetDate: '2025-08-15', 
              paymentAccountId: 'Main Savings',
              destinationAccountId: 'Main Savings',
              notes: 'Summer vacation to Europe'
            }
          ],
          notes: [
            'TargetAmount and currentAmount should be positive numbers',
            'Target amount can be larger than current account balance',
            'CurrentAmount can start at 0 and be increased over time',
            'TargetDate must be in YYYY-MM-DD format',
            'PaymentAccountId and DestinationAccountId are required and should match account names',
            'You can set savings goals that exceed your current account balance',
            'Use the detailed view to add contributions and track progress',
            'Notes help you remember the purpose of each savings goal'
          ]
        };
      case 'debts':
        return {
          title: 'Debts Import Instructions',
          description: 'Upload your debt data using CSV, Excel, or JSON format',
          requiredFields: [
            { field: 'title', description: 'Debt title (e.g., "Car Loan", "Credit Card")', required: true },
            { field: 'type', description: 'Debt type (auto, credit_card, personal, student, mortgage, other)', required: true },
            { field: 'initialAmount', description: 'Initial debt amount (numeric value)', required: true },
            { field: 'annualInterestRate', description: 'Annual interest rate as percentage (e.g., 5.5)', required: true },
            { field: 'startDate', description: 'Start date in YYYY-MM-DD format', required: true },
            { field: 'dueDate', description: 'Due date in YYYY-MM-DD format', required: true },
            { field: 'amountRepaid', description: 'Amount already repaid (numeric, can be 0)', required: false },
            { field: 'monthlyPayment', description: 'Monthly payment amount (numeric)', required: true },
            { field: 'monthlyPaymentDueDate', description: 'Monthly payment due date (YYYY-MM-DD)', required: false },
            { field: 'status', description: 'Debt status (active, completed, archived, delinquent)', required: false },
            { field: 'paymentAccountId', description: 'Payment account name (optional)', required: false },
            { field: 'notes', description: 'Notes about the debt (optional)', required: false }
          ],
          sampleData: [
            { 
              title: 'Car Loan', 
              type: 'auto',
              initialAmount: '25000', 
              annualInterestRate: '5.5',
              startDate: '2024-01-01',
              dueDate: '2029-01-01',
              amountRepaid: '5000', 
              monthlyPayment: '450',
              monthlyPaymentDueDate: '15',
              status: 'active',
              paymentAccountId: 'Main Checking',
              notes: 'Car loan for Honda Civic'
            },
            { 
              title: 'Credit Card', 
              type: 'credit_card',
              initialAmount: '8000', 
              annualInterestRate: '18.9',
              startDate: '2023-06-01',
              dueDate: '2026-06-01',
              amountRepaid: '2000', 
              monthlyPayment: '300',
              monthlyPaymentDueDate: '5',
              status: 'active',
              paymentAccountId: 'Main Checking',
              notes: 'High interest credit card debt'
            },
            { 
              title: 'Student Loan', 
              type: 'student',
              initialAmount: '35000', 
              annualInterestRate: '4.2',
              startDate: '2020-09-01',
              dueDate: '2030-09-01',
              amountRepaid: '12000', 
              monthlyPayment: '350',
              monthlyPaymentDueDate: '1',
              status: 'active',
              paymentAccountId: 'Main Checking',
              notes: 'Federal student loan'
            }
          ],
          notes: [
            'InitialAmount, annualInterestRate, amountRepaid, and monthlyPayment should be positive numbers',
            'Type should be one of: auto, credit_card, personal, student, mortgage, other',
            'Status should be one of: active, completed, archived, delinquent',
            'Dates must be in YYYY-MM-DD format',
            'AmountRepaid can start at 0 and be increased over time through payments',
            'PaymentAccountId should match existing account names',
            'Use the detailed view to record payments and track progress',
            'The system will auto-calculate balance and estimated final payment date'
          ]
        };
      case 'mortgages':
        return {
          title: 'Mortgages Import Instructions',
          description: 'Upload your mortgage data using CSV, Excel, or JSON format',
          requiredFields: [
            { field: 'propertyName', description: 'Property name or title (e.g., "Main Residence", "Investment Property")', required: true },
            { field: 'initialAmount', description: 'Initial mortgage amount (numeric value)', required: true },
            { field: 'propertyValue', description: 'Current estimated property value (numeric)', required: true },
            { field: 'currency', description: 'Currency code (e.g., USD, EUR, GBP)', required: true },
            { field: 'annualInterestRate', description: 'Annual interest rate as percentage (e.g., 3.5)', required: true },
            { field: 'mortgageStartDate', description: 'Mortgage start date in YYYY-MM-DD format', required: true },
            { field: 'mortgageDueDate', description: 'Mortgage due date in YYYY-MM-DD format', required: true },
            { field: 'amountRepaid', description: 'Amount already repaid (numeric, can be 0)', required: false },
            { field: 'monthlyPayment', description: 'Monthly payment amount (numeric)', required: true },
            { field: 'monthlyPaymentDueDate', description: 'Monthly payment due date (day of month, e.g., 1, 15)', required: false },
            { field: 'status', description: 'Mortgage status (active, completed, archived)', required: false },
            { field: 'paymentAccountId', description: 'Payment account name', required: false },
            { field: 'notes', description: 'Notes about the mortgage (optional)', required: false }
          ],
          sampleData: [
            { 
              propertyName: 'Main Residence', 
              initialAmount: '350000', 
              propertyValue: '420000',
              annualInterestRate: '3.5',
              mortgageStartDate: '2020-01-15',
              mortgageDueDate: '2030-01-15',
              amountRepaid: '75000', 
              monthlyPayment: '1850',
              monthlyPaymentDueDate: '1',
              status: 'active',
              paymentAccountId: 'Main Checking',
              notes: 'Primary residence mortgage'
            },
            { 
              propertyName: 'Investment Property', 
              initialAmount: '250000', 
              propertyValue: '280000',
              annualInterestRate: '4.2',
              mortgageStartDate: '2021-06-01',
              mortgageDueDate: '2030-01-15',
              amountRepaid: '35000', 
              monthlyPayment: '1350',
              monthlyPaymentDueDate: '15',
              status: 'active',
              paymentAccountId: 'Investment Account',
              notes: 'Rental property investment'
            },
            { 
              propertyName: 'Vacation Home', 
              initialAmount: '180000', 
              propertyValue: '210000',
              annualInterestRate: '4.8',
              mortgageStartDate: '2019-03-01',
              mortgageDueDate: '2030-01-15',
              amountRepaid: '65000', 
              monthlyPayment: '1120',
              monthlyPaymentDueDate: '5',
              status: 'active',
              paymentAccountId: 'Main Checking',
              notes: 'Mountain cabin mortgage'
            }
          ],
          notes: [
            'InitialAmount, propertyValue, annualInterestRate, amountRepaid, and monthlyPayment should be positive numbers',
            'PropertyValue should reflect current market value of the property',
            'Status should be one of: active, completed, archived, delinquent',
            'Dates must be in DD-MM-YYYY format',
            'AmountRepaid can start at 0 and be increased over time through payments',
            'PaymentAccountId should match existing account names',
            'Use the detailed view to record payments and track equity growth',
            'The system will auto-calculate balance, equity, and estimated final payment date',
            'Property value helps track equity growth over time'
          ]
        };
      case 'investments':
        return {
          title: 'Investments Import Instructions',
          description: 'Upload your investment transactions using CSV, Excel, or JSON format',
          requiredFields: [
            { field: 'assetName', description: 'Investment asset name (e.g., "Apple Inc.", "Bitcoin")', required: true },
            { field: 'ticker', description: 'Ticker symbol (e.g., "AAPL", "BTC-USD", "RIO.L") - used to fetch live prices', required: true },
            { field: 'type', description: 'Investment type (Stock, ETF, Crypto, Bond, Fund, Other)', required: true },
            { field: 'trade', description: 'Transaction type (buy, sell)', required: true },
            { field: 'units', description: 'Number of units/shares (numeric, supports decimals for crypto)', required: true },
            { field: 'cost', description: 'Total cost of the trade including order fees (numeric)', required: true },
            { field: 'orderPrice', description: 'Price per unit at time of transaction (numeric)', required: true },
            { field: 'currency', description: 'Currency code (e.g., USD, EUR, GBP)', required: true },
            { field: 'date', description: 'Transaction date in YYYY-MM-DD format', required: true },
            { field: 'paymentAccountId', description: 'Payment account name (must match existing account)', required: true },
            { field: 'notes', description: 'Transaction notes (optional)', required: false }
          ],
          sampleData: [
            { 
              assetName: 'Apple Inc.',
              ticker: 'AAPL',
              type: 'Stock',
              trade: 'buy',
              units: '10',
              cost: '1505.00',
              orderPrice: '150.00',
              currency: 'USD',
              date: '2024-01-15',
              paymentAccountId: 'Investment Account',
              notes: 'Initial purchase'
            },
            { 
              assetName: 'Vanguard S&P 500 ETF',
              ticker: 'VOO',
              type: 'ETF',
              trade: 'buy',
              units: '5',
              cost: '2009.95',
              orderPrice: '400.00',
              currency: 'USD',
              date: '2024-02-01',
              paymentAccountId: 'Investment Account',
              notes: 'Monthly investment'
            },
            { 
              assetName: 'Bitcoin',
              ticker: 'BTC-USD',
              type: 'Crypto',
              trade: 'buy',
              units: '0.1',
              cost: '4525.00',
              orderPrice: '45000.00',
              currency: 'USD',
              date: '2024-02-15',
              paymentAccountId: 'Crypto Account',
              notes: 'Crypto diversification'
            },
            { 
              assetName: 'Rio Tinto Group',
              ticker: 'RIO.L',
              type: 'Stock',
              trade: 'buy',
              units: '25',
              cost: '1280.50',
              orderPrice: '51.00',
              currency: 'GBP',
              date: '2024-03-01',
              paymentAccountId: 'Investment Account',
              notes: 'UK stock purchase'
            }
          ],
          notes: [
            'Units and orderPrice should be positive numbers',
            'Units can have up to 18 decimal places for crypto assets (e.g., 0.00123456)',
            'Trade should be either "buy" or "sell" (case-insensitive)',
            'Type should be one of: Stock, ETF, Crypto, Bond, Fund, Other',
            'Currency must be a valid 3-letter code (USD, EUR, GBP, etc.)',
            'Ticker symbol must be correct for live price updates (e.g., AAPL for Apple, BTC-USD for Bitcoin)',
            'For international stocks, use the correct suffix (e.g., RIO.L for London Stock Exchange)',
            'Dates must be in YYYY-MM-DD format',
            'PaymentAccountId must match an existing account name from your accounts',
            'Cost = (units × orderPrice) + fees/commissions',
            'The system will auto-calculate portfolio metrics and P/L using live market prices',
            'Historical transactions can be uploaded regardless of current account balances',
            'Total units = sum of buy units minus sum of sell units',
            'Average cost per unit = total cost ÷ total units',
            'Click on any asset in the portfolio to view detailed transactions and add new ones'
          ]
        };
      case 'subscription':
        return {
          title: 'Subscription Import Instructions',
          description: 'Upload your subscription data using CSV, Excel, or JSON format',
          requiredFields: [
            { field: 'itemName', description: 'Subscription item name (e.g., "Netflix")', required: true },
            { field: 'category', description: 'Subscription category (e.g., "Entertainment")', required: true },
            { field: 'frequency', description: 'Frequency: Monthly, Yearly', required: true },
            { field: 'amount', description: 'Subscription amount (numeric value)', required: true },
            { field: 'subscriptionDate', description: 'Subscription date in YYYY-MM-DD format', required: true },
            { field: 'renewalDate', description: 'Renewal date in YYYY-MM-DD format', required: true },
            { field: 'account', description: 'Account name for payment', required: true }
          ],
          sampleData: [
            { 
              itemName: 'Netflix', 
              category: 'Entertainment', 
              frequency: 'Monthly', 
              amount: '15', 
              subscriptionDate: '2025-01-01',
              renewalDate: '2025-02-01',
              account: 'Subscription Account'
            }
          ],
          notes: [
            'Amount should be a positive number',
            'Frequency must be either Monthly or Yearly',
            'SubscriptionDate and renewalDate must be in YYYY-MM-DD format',
            'Account must be a valid account name from your accounts',
            'Use the detailed view to manage subscription changes and track payments'
          ]
        };
      default:
        return null;
    }
  };

  const instructions = getInstructions();
  if (!instructions) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FiFileText className="w-5 h-5 text-btn-secondary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{instructions.title}</h2>
              <p className="text-sm text-gray-600">{instructions.description}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white rounded-lg transition-colors"
          >
            <FiX className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="mb-6">
            <button
              onClick={onDownloadTemplate}
              className="flex items-center gap-2 bg-btn-primary text-white px-4 py-2 rounded-lg hover:bg-btn-primary-hover transition-colors"
            >
              <FiDownload className="w-4 h-4" />
              Download Template
            </button>
            <p className="text-sm text-gray-600 mt-2">
              Download a pre-formatted template with sample data to get started quickly.
            </p>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Required Fields</h3>
            <div className="space-y-3">
              {instructions.requiredFields.map((field, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0">
                    {field.required ? (
                      <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                    ) : (
                      <div className="w-2 h-2 bg-gray-400 rounded-full mt-2"></div>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{field.field}</span>
                      {field.required && (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">Required</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">{field.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Sample Data Format</h3>
            <div className="bg-gray-50 rounded-lg p-4 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    {Object.keys(instructions.sampleData[0]).map(key => (
                      <th key={key} className="text-left py-2 px-3 font-medium text-gray-700">
                        {key}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {instructions.sampleData.map((row, index) => (
                    <tr key={index} className="border-b border-gray-100">
                      {Object.values(row).map((value, idx) => (
                        <td key={idx} className="py-2 px-3 text-gray-600">
                          {value}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <FiAlertCircle className="w-5 h-5 text-amber-500" />
              Important Notes
            </h3>
            <ul className="space-y-2">
              {instructions.notes.map((note, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></span>
                  {note}
                </li>
              ))}
            </ul>
          </div>

          {/* Supported Formats */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Supported File Formats</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
              <span className="bg-white px-3 py-1 rounded text-blue-700">.csv</span>
              <span className="bg-white px-3 py-1 rounded text-blue-700">.xlsx</span>
              <span className="bg-white px-3 py-1 rounded text-blue-700">.xls</span>
              <span className="bg-white px-3 py-1 rounded text-blue-700">.json</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstructionsModal;